import React, { useContext, useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useQuery } from '../../../routes'
import { Layout, Navigation, Responsive, Input, Button, Indicators } from 'ui'
import {
  useResourceCategory,
  useCreateResourceCategoryMutation,
  useUpdateResourceCategoryMutation,
  useDeleteResourceCategoryMutation,
} from '../../../queries'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-hot-toast'

interface CategoryFormData {
  name: string
}

// Validation schema using yup
const categorySchema = yup.object({
  name: yup.string().required('Name is required'),
})

export const ResourceCategoryDetailView: React.FC = () => {
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')

  const { id } = useParams<{ id: string }>()
  const { set, search } = useQuery<{ modal: string }>()
  const navigate = useNavigate()

  const isNewCategory = id === 'new'
  const { data, isLoading: loading } = useResourceCategory(
    isNewCategory ? '' : id!
  )
  const [
    createResourceCategory,
    { loading: createLoading },
  ] = useCreateResourceCategoryMutation()
  const [
    updateResourceCategory,
    { loading: updateLoading },
  ] = useUpdateResourceCategoryMutation()
  const [
    deleteResourceCategory,
    { loading: deleteLoading },
  ] = useDeleteResourceCategoryMutation()

  const category = useMemo(() => (isNewCategory ? null : data?.category), [
    isNewCategory,
    data,
  ])

  const documents = category?.documents ?? []
  const [modalError, setModalError] = useState<string | null>(null)

  // Form setup with React Hook Form
  const form = useForm<CategoryFormData>({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: '',
    },
  })

  // Update form when category data loads
  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name || '',
      })
    }
  }, [category, form])

  // Handle form submission
  const handleSubmit = async (values: CategoryFormData) => {
    try {
      const result = isNewCategory
        ? await createResourceCategory({
            variables: {
              payload: {
                name: values.name,
              },
            },
          })
        : await updateResourceCategory({
            variables: {
              categoryId: id!,
              payload: {
                name: values.name,
              },
            },
          })

      if (result.success && result.category) {
        if (isNewCategory) {
          toast.success('Category created successfully')
          navigate(`/admin/resources/categories/show/${result.category.id}`)
        } else {
          toast.success('Category updated successfully')
        }
      } else {
        toast.error(result.errors?.[0]?.message || 'Failed to save category')
      }
    } catch (error) {
      console.error('Error saving category:', error)
      toast.error('Failed to save category')
    }
  }
  const handleDestroy = async () => {
    try {
      const result = await deleteResourceCategory({
        variables: { categoryId: id! },
      })
      if (result.success) {
        toast.success('Category deleted successfully')
        navigate('/admin/resources/categories')
      } else {
        const errors = result.errors || []
        setModalError(
          errors[0]?.message ?? 'There was a problem deleting this category.'
        )
      }
    } catch (error) {
      setModalError('There was a problem deleting this category.')
    }
  }

  return (
    <Layout.PaddedContent className="pb-24 md:pb-32">
      <Helmet>
        <title>
          {isNewCategory ? 'New Category' : category?.name || 'Category'} - MCSS
          Pro Connect
        </title>
      </Helmet>

      <div className={`max-w-4xl mx-auto ${loading ? 'opacity-50' : ''}`}>
        <div className="mb-6">
          <Navigation.ReturnLink to={`/admin/resources/categories`}>
            Return to Categories
          </Navigation.ReturnLink>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isNewCategory ? 'Create New Category' : 'Edit Category'}
              </h1>
              <p className="text-gray-600">
                {isNewCategory
                  ? 'Create a new resource category'
                  : 'Update the category information below'}
              </p>
            </div>

            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              {/* Name Field */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Category Name *
                </label>
                <Input
                  id="name"
                  {...form.register('name')}
                  placeholder="Enter category name"
                  disabled={createLoading || updateLoading}
                  className="mt-1"
                />
                {form.formState.errors.name && (
                  <div className="text-red-600 text-sm mt-1">
                    {form.formState.errors.name.message}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-between pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/resources/categories')}
                  disabled={createLoading || updateLoading}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={createLoading || updateLoading}
                  className="ml-4"
                >
                  {(createLoading || updateLoading) && (
                    <Indicators.Spinner size="sm" className="mr-2" />
                  )}
                  {isNewCategory ? 'Create Category' : 'Save Changes'}
                </Button>
              </div>
            </form>

            {/* Delete Section for existing categories */}
            {!isNewCategory && category && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-red-900 mb-2">
                    Danger Zone
                  </h3>
                  {documents.length > 0 ? (
                    <p className="text-sm text-red-700 mb-4">
                      You cannot delete this category as it currently has{' '}
                      {documents.length} associated document(s).
                    </p>
                  ) : (
                    <p className="text-sm text-red-700 mb-4">
                      Permanently delete this category. This action cannot be
                      undone.
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={documents.length > 0 || deleteLoading}
                    onClick={() => set({ modal: 'destroy' })}
                  >
                    {deleteLoading && (
                      <Indicators.Spinner size="sm" className="mr-2" />
                    )}
                    Delete Category
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {search.modal === 'destroy' && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Category?
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    {modalError ||
                      "Are you sure you'd like to delete this category? This cannot be undone."}
                  </p>
                </div>
                <div className="flex justify-center space-x-4 px-4 py-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      set({ modal: '' })
                      setModalError(null)
                    }}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDestroy}
                    disabled={deleteLoading}
                  >
                    {deleteLoading && (
                      <Indicators.Spinner size="sm" className="mr-2" />
                    )}
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout.PaddedContent>
  )
}
